// formUtils.js

import React, { useState, useCallback, useEffect, useRef, useContext } from "react";
import { MyContext } from "../../../../Context/MyContext";
import InputBox   from "./InputBox";
import Dropdwon   from "./Dropdwon";
import FileUpload from "./FileUpload";
import AppIcon from "../../../../Components/AppIcon";
import API, { BASE_URL } from "../../../../Api/api";

const companyLogoSrc = (logo) => {
  if (!logo) return '';
  const s = String(logo);
  if (/^https?:\/\//i.test(s) || s.startsWith('blob:') || s.startsWith('data:')) return s;
  const base = String(BASE_URL || '').replace(/\/+$/, '');
  const path = s.replace(/^\/+/, '');
  return `${base}/${path.startsWith('uploads/') ? path : `uploads/${path}`}`;
};

// ═══════════════════════════════════════════════════════════════
// VISIBILITY + REQUIRED
// ═══════════════════════════════════════════════════════════════
export const fieldVisible = (f, typeId, normalId, isOther) => {
  if (!f.is_show) return false;
  const hasOtherShow  = f.other_show  && f.other_show.length  > 0;
  const hasCategoryId = f.category_id && f.category_id.length > 0;
  const hasTypeId     = f.type_id     && f.type_id.length     > 0;

  if (isOther) {
    if (hasOtherShow) return typeId != null && f.other_show.includes(typeId);
    if (hasCategoryId) return false;
    const typeOk = !hasTypeId || (typeId && f.type_id.includes(typeId));
    return typeOk;
  }
  if (hasOtherShow && !hasCategoryId) return false;
  const typeOk = !hasTypeId || (typeId && f.type_id.includes(typeId));
  const normOk = !hasCategoryId || (normalId && f.category_id.includes(normalId));
  return typeOk && normOk;
};

export const fieldRequired = (f, typeId, normalId, isOther) => {
  if (isOther) {
    return f.other_required && f.other_required.length > 0 ? f.other_required.includes(typeId) : false;
  }
  return f.category_required && f.category_required.length > 0 && normalId
    ? f.category_required.includes(normalId) : false;
};

// ═══════════════════════════════════════════════════════════════
// SAFE SERIALIZE
// ═══════════════════════════════════════════════════════════════
const safeSerialize = (data) => {
  const strip = (v) => {
    if (v instanceof File || v instanceof Blob) return null;
    if (Array.isArray(v)) return v.map(strip).filter(x => x !== null);
    if (v && typeof v === "object" && !(v instanceof Date)) {
      const out = {};
      for (const [k, val] of Object.entries(v)) out[k] = strip(val);
      return out;
    }
    return v;
  };
  return JSON.stringify(strip(data));
};

// ═══════════════════════════════════════════════════════════════
// DRAFT HOOK
// ═══════════════════════════════════════════════════════════════
export const useDraft = (key, { enabled = true } = {}) => {
  const [formData, setFormData] = useState(() => {
    if (!enabled) return {};
    try { return JSON.parse(sessionStorage.getItem(key) || "{}"); } catch { return {}; }
  });
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftSaved,     setDraftSaved]     = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Object.keys(parsed).length > 0) setShowDraftModal(true);
      } catch {}
    }
    window.history.pushState({ draftPage: true }, "");
  }, [key, enabled]); // eslint-disable-line

  // Debounce draft persistence so rapid form seeding / typing doesn't block the UI thread
  useEffect(() => {
    if (!enabled) return;
    if (Object.keys(formData).length === 0) return undefined;
    const timer = window.setTimeout(() => {
      try { sessionStorage.setItem(key, safeSerialize(formData)); } catch {}
    }, 180);
    return () => window.clearTimeout(timer);
  }, [formData, key, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handlePopState = () => {
      if (Object.keys(formData).length > 0) {
        window.history.pushState({ draftPage: true }, "");
        try { localStorage.setItem(key, safeSerialize(formData)); } catch {}
        setShowDraftModal(true);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [formData, key, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handleUnload = () => {
      if (Object.keys(formData).length > 0)
        try { localStorage.setItem(key, safeSerialize(formData)); } catch {}
    };
    window.addEventListener("unload", handleUnload);
    return () => window.removeEventListener("unload", handleUnload);
  }, [formData, key, enabled]);

  const onChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDraftSaved(false);
  }, []);

  const saveDraft    = () => { try { localStorage.setItem(key, safeSerialize(formData)); } catch {} setDraftSaved(true); };
  const restoreDraft = () => {
    try { const s = localStorage.getItem(key); if (s) setFormData(JSON.parse(s)); } catch {}
    setShowDraftModal(false);
    window.history.pushState({ draftPage: true }, "");
  };
  const discardDraft = () => {
    localStorage.removeItem(key); sessionStorage.removeItem(key);
    setShowDraftModal(false); setFormData({});
  };
  const clearDraft = () => { localStorage.removeItem(key); sessionStorage.removeItem(key); };

  return { formData, setFormData, onChange, saveDraft, draftSaved, setDraftSaved,
    showDraftModal, restoreDraft, discardDraft, clearDraft };
};

// ═══════════════════════════════════════════════════════════════
// RANGE FIELD — price / bhk போன்ற plain number fields
// fieldCfg.range = true + landtype = false
// ═══════════════════════════════════════════════════════════════
const numberToWords = (num) => {
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (num === 0) return "Zero";
  const convert = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return `${b[Math.floor(n / 10)]} ${a[n % 10]}`.trim();
    if (n < 1000) return `${a[Math.floor(n / 100)]} Hundred ${convert(n % 100)}`.trim();
    if (n < 100000) return `${convert(Math.floor(n / 1000))} Thousand ${convert(n % 1000)}`.trim();
    if (n < 10000000) return `${convert(Math.floor(n / 100000))} Lakh ${convert(n % 100000)}`.trim();
    return `${convert(Math.floor(n / 10000000))} Crore ${convert(n % 10000000)}`.trim();
  };
  return convert(Math.floor(Number(num) || 0)).trim();
};

const priceToWords = (n) => {
  if (!n || isNaN(n) || Number(n) <= 0) return "";
  return `${numberToWords(Number(n))} Only`;
};

export const RangeField = ({ fieldCfg, formData, onChange, errors, req }) => {
  const {
    label,
    range_from_field,
    range_to_field,
    range_from_placeholder = "Min",
    range_to_placeholder   = "Max",
    is_price_field,
  } = fieldCfg;

  const fromVal = formData[range_from_field] ?? "";
  const toVal   = formData[range_to_field]   ?? "";
  const showPrice = Boolean(is_price_field);

  const PriceHint = ({ value }) => {
    if (!showPrice || !value) return null;
    const words = priceToWords(value);
    if (!words) return null;
    return (
      <div className="lt-price-hints">
        <span className="lt-price-words">{words}</span>
      </div>
    );
  };

  return (
    <div className="range-field-wrap">
      <label className="form-label">
        {label}{req && <span className="required-star">*</span>}
      </label>
      <div className="range-inputs-row">
        <div className="range-input-col">
          <InputBox
            field={range_from_field}
            type="number"
            placeholder={range_from_placeholder}
            value={fromVal}
            onChange={onChange}
            error={errors?.[range_from_field]}
          />
          <PriceHint value={fromVal} />
        </div>
        <span className="range-sep">—</span>
        <div className="range-input-col">
          <InputBox
            field={range_to_field}
            type="number"
            placeholder={range_to_placeholder}
            value={toVal}
            onChange={onChange}
            error={errors?.[range_to_field]}
          />
          <PriceHint value={toVal} />
        </div>
      </div>
      {(errors?.[range_from_field] || errors?.[range_to_field]) && (
        <span className="error-msg">
          {errors[range_from_field] || errors[range_to_field]}
        </span>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// RANGE LAND TYPE FIELD — total_area போன்ற landtype + range fields
// from/to இரண்டும் same unit selector share பண்ணும்
// ═══════════════════════════════════════════════════════════════
export const RangeLandTypeField = ({ fieldCfg, formData, onChange, errors, req }) => {
  const { landtype } = useContext(MyContext);
  const allLt = Array.isArray(landtype) && landtype.length > 0 ? landtype : [];
  const lt = (() => {
    if (!fieldCfg.landtype_ids?.length) return allLt;
    const allowed = new Set(fieldCfg.landtype_ids.map(Number));
    const filtered = allLt.filter((l) => allowed.has(Number(l.id)));
    return filtered.length ? filtered : allLt;
  })();

  const {
    label,
    field,
    range_from_field,
    range_to_field,
    range_from_placeholder = "Min Area",
    range_to_placeholder   = "Max Area",
  } = fieldCfg;

  const unitKey = field + "_unit";
  const selLT   = formData[unitKey]
    ? lt.find(l => Number(l.id) === Number(formData[unitKey])) || lt[0]
    : lt[0];

  useEffect(() => {
    const hasRange = formData[range_from_field] || formData[range_to_field];
    if (!hasRange || selLT?.id == null) return;
    if (formData[unitKey] != null && formData[unitKey] !== "") return;
    onChange(unitKey, selLT.id);
  }, [formData[range_from_field], formData[range_to_field], selLT?.id, formData[unitKey]]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="range-field-wrap">
      <label className="form-label">
        {label}{req && <span className="required-star">*</span>}
      </label>
      <div className="range-inputs-row range-landtype-row">
        <div className="range-input-col">
          <InputBox
            field={range_from_field}
            type="number"
            placeholder={range_from_placeholder}
            value={formData[range_from_field] ?? ""}
            onChange={onChange}
            error={errors?.[range_from_field]}
          />
        </div>
        <span className="range-sep">—</span>
        <div className="range-input-col">
          <InputBox
            field={range_to_field}
            type="number"
            placeholder={range_to_placeholder}
            value={formData[range_to_field] ?? ""}
            onChange={onChange}
            error={errors?.[range_to_field]}
          />
        </div>
        <div className="lt-unit-sel">
          <Dropdwon
            field={unitKey}
            options={lt}
            value={selLT?.id}
            onChange={(_, val, opt) => onChange(unitKey, opt.id)}
            labelKey="landtype_name"
            valueKey="id"
          />
        </div>
      </div>
      {(errors?.[range_from_field] || errors?.[range_to_field]) && (
        <span className="error-msg">
          {errors[range_from_field] || errors[range_to_field]}
        </span>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// LAND TYPE FIELD (single — no range)
// ═══════════════════════════════════════════════════════════════
export const LandTypeField = ({ fieldCfg, formData, onChange, errors, req, totalPrice }) => {
  const { landtype } = useContext(MyContext);
  const lt = Array.isArray(landtype) && landtype.length > 0 ? landtype : [];
  const { field, label } = fieldCfg;
  const unitKey = field + "_unit";
  const getFilteredUnits = () => {
    if (!fieldCfg.landtype_ids || !fieldCfg.landtype_ids.length) return lt;
    const allowed = new Set(fieldCfg.landtype_ids.map(Number));
    const filtered = lt.filter(l => allowed.has(Number(l.id)));
    return filtered.length ? filtered : lt;
  };
  const units = getFilteredUnits();
  const selLT   = formData[unitKey]
    ? units.find(l => Number(l.id) === Number(formData[unitKey])) || units[0]
    : units[0];
  const amount  = formData[field] || "";

  useEffect(() => {
    if (!amount || selLT?.id == null) return;
    if (formData[unitKey] != null && formData[unitKey] !== "") return;
    onChange(unitKey, selLT.id);
  }, [amount, selLT?.id, formData[unitKey]]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (n) => {
    if (!n || isNaN(n)) return "";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
    return `₹${Math.round(n).toLocaleString("en-IN")}`;
  };

  const sqftBase = (() => {
    if (!amount || !selLT) return null;
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) return null;
    return num * (selLT.calculation?.to_sqft ?? 1);
  })();

  const pricePerSqft = (totalPrice && sqftBase) ? totalPrice / sqftBase : null;

  const badges = (() => {
    if (!sqftBase || !lt.length) return [];
    const sqftLT = lt.find(l => l.id === 1);
    return lt.map(ltItem => {
      const toKey  = `to_${ltItem.landtype_name.replace(/-/g, "")}`;
      const factor = sqftLT?.calculation[toKey] ?? null;
      if (!factor) return null;
      const areaVal = +(sqftBase * factor).toFixed(4);
      const ppu = pricePerSqft && ltItem.calculation?.to_sqft
        ? Math.round(pricePerSqft * ltItem.calculation.to_sqft) : null;
      return { id: ltItem.id, unitName: ltItem.landtype_name, areaVal, ppu, isCurrent: ltItem.id === selLT?.id };
    }).filter(Boolean);
  })();

  const currentPpu = badges.find(b => b.isCurrent)?.ppu;

  return (
    <div className="landtype-wrap">
      <label className="form-label">{label}{req && <span className="required-star">*</span>}</label>
      <div className="lt-row">
        <InputBox field={field} type="number" placeholder="Enter value"
          value={amount} onChange={onChange} error={errors?.[field]} />
        <div className="lt-unit-sel">
          <Dropdwon field={unitKey} options={units} value={selLT?.id}
            onChange={(_, val, opt) => onChange(unitKey, opt.id)}
            labelKey="landtype_name" valueKey="id" />
        </div>
      </div>
      {currentPpu && <div className="lt-price-rate">{fmt(currentPpu)} / {selLT?.landtype_name}</div>}
      {errors?.[field] && <span className="error-msg">{errors[field]}</span>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// RENDER FIELD
// ✅ range check — landtype+range → RangeLandTypeField
// ✅ range check — plain number  → RangeField
// ═══════════════════════════════════════════════════════════════
export const RenderField = ({ fieldCfg, formData, onChange, errors, req, totalPrice }) => {
  const { dropdown } = useContext(MyContext);
  const { field, label, type, placeholder } = fieldCfg;
  const err = errors?.[field];

  // ✅ 1. Range + landtype (e.g. total_area)
  if (fieldCfg.landtype && fieldCfg.range)
    return <RangeLandTypeField fieldCfg={fieldCfg} formData={formData}
      onChange={onChange} errors={errors} req={req} />;

  // ✅ 2. Range plain number (e.g. price, bhk)
  if (fieldCfg.range)
    return <RangeField fieldCfg={fieldCfg} formData={formData}
      onChange={onChange} errors={errors} req={req} />;

  // 3. Single landtype
  if (fieldCfg.landtype)
    return <LandTypeField fieldCfg={fieldCfg} formData={formData}
      onChange={onChange} errors={errors} req={req}
      totalPrice={totalPrice || formData.price || null} />;

  // 4. Dropdown
  if (fieldCfg.dropdown && fieldCfg.dropdown_id) {
    const dd = Array.isArray(dropdown) ? dropdown.find(d => d.id === fieldCfg.dropdown_id) : null;
    return <Dropdwon label={label} field={field} options={dd?.options || []}
      value={formData[field] ?? ""} onChange={onChange}
      placeholder={placeholder} isrequired={req} error={err} />;
  }

  // 5. File — with crop support for thumbnail field
  if (type === "file")
    return <FileUpload label={label} field={field} value={formData[field]}
      onChange={onChange} isrequired={req}
      multi_upload={fieldCfg.multi_upload} accept={fieldCfg.accept || "image/*"} error={err}
      enableCrop={field === "thumbnail" && !fieldCfg.multi_upload}
      cropAspect={4 / 3}
    />;

  // 6. Default input
  return <InputBox label={label} field={field} type={type} placeholder={placeholder}
    value={formData[field] || ""} onChange={onChange} isrequired={req} error={err} />;
};

// ═══════════════════════════════════════════════════════════════
// DOCUMENTS BLOCK — Layout & Brochure (image multi / PDF)
// ═══════════════════════════════════════════════════════════════
export const DocumentsBlock = ({ formData, onChange }) => {
  return (
    <div className="documents-block-wrap">
      <DocumentSection
        label="Layout"
        typeField="layout_type"
        fileField="layout"
        formData={formData}
        onChange={onChange}
      />
      <DocumentSection
        label="Brochure"
        typeField="brochure_type"
        fileField="brochure"
        formData={formData}
        onChange={onChange}
      />
    </div>
  );
};

// Internal — ஒவ்வொரு document section-க்கும்
const DocumentSection = ({ label, typeField, fileField, formData, onChange }) => {
  const docType = formData[typeField] || "image";
  const isImage = docType === "image";

  const handleTypeChange = (val) => {
    onChange(typeField, val);
    onChange(fileField, val === "image" ? [] : null);
  };

  return (
    <div className="doc-section">
      <div className="doc-section-header">
        <span className="doc-section-label">{label}</span>
        <div className="doc-type-toggle">
          {["image", "pdf"].map((opt) => (
            <button
              key={opt}
              type="button"
              className={`doc-type-btn${docType === opt ? " active" : ""}`}
              onClick={() => handleTypeChange(opt)}
            >
              {opt === "image" ? (
                <><AppIcon name="image" size={14} /> Images</>
              ) : (
                <><AppIcon name="pdf" size={14} /> PDF</>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="doc-upload-area">
        {isImage ? (
          <FileUpload
            label=""
            field={fileField}
            value={formData[fileField]}
            onChange={onChange}
            multi_upload={true}
            accept="image/*"
          />
        ) : (
          <FileUpload
            label=""
            field={fileField}
            value={formData[fileField]}
            onChange={onChange}
            multi_upload={false}
            accept="application/pdf"
          />
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// VIDEO BLOCK
// ═══════════════════════════════════════════════════════════════
export const VideoBlock = ({ formData, onChange }) => {
  const videos = Array.isArray(formData.videos) ? formData.videos : [];
  const shorts = Array.isArray(formData.shorts) ? formData.shorts : [];

  const addVideo = () => onChange("videos", [...videos, { id: Date.now(), thumbnail: null, video_link: "" }]);
  const rmVideo  = id => onChange("videos", videos.filter(v => v.id !== id));

  const updVideoThumb = (id, val) =>
    onChange("videos", videos.map(v => v.id === id ? { ...v, thumbnail: val } : v));
  const updVideoLink = (id, val) =>
    onChange("videos", videos.map(v => v.id === id ? { ...v, video_link: val } : v));

  const addShort = () => onChange("shorts", [...shorts, { id: Date.now(), short_link: "" }]);
  const rmShort  = id => onChange("shorts", shorts.filter(s => s.id !== id));
  const updShort = (id, val) =>
    onChange("shorts", shorts.map(s => s.id === id ? { ...s, short_link: val } : s));

  return (
    <div className="video-block-wrap">
      <div className="video-section">
        <div className="video-section-header">
          <span className="video-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
            </svg>Videos
          </span>
          <button type="button" className="add-video-btn" onClick={addVideo}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>Add Video
          </button>
        </div>
        {videos.length === 0
          ? <div className="video-empty-hint">No videos added. Click "Add Video" to add.</div>
          : <div className="video-list">{videos.map((item, i) => (
            <div key={item.id} className="video-card">
              <div className="video-card-header">
                <span className="vc-num">Video #{i + 1}</span>
                <button type="button" className="vc-rm" onClick={() => rmVideo(item.id)}>Remove</button>
              </div>
              <div className="video-card-grid">
                <FileUpload
                  label="Thumbnail Image"
                  field={`vt_${item.id}`}
                  value={item.thumbnail}
                  onChange={(_, val) => updVideoThumb(item.id, val)}
                  multi_upload={false}
                  accept="image/*"
                />
                <InputBox
                  label="YouTube Video Link"
                  field={`vl_${item.id}`}
                  type="text"
                  placeholder="https://youtube.com/watch?v=..."
                  value={item.video_link || ""}
                  onChange={(_, val) => updVideoLink(item.id, val)}
                />
              </div>
            </div>
          ))}</div>
        }
      </div>
      <div className="video-section">
        <div className="video-section-header">
          <span className="video-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <rect x="5" y="2" width="14" height="20" rx="2"/>
              <line x1="12" y1="9" x2="12" y2="15"/><line x1="9" y1="12" x2="15" y2="12"/>
            </svg>YouTube Shorts
          </span>
          <button type="button" className="add-short-btn" onClick={addShort}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>Add Short
          </button>
        </div>
        {shorts.length === 0
          ? <div className="video-empty-hint">No shorts added. Click "Add Short" to add.</div>
          : <div className="shorts-list">{shorts.map((item, i) => (
            <div key={item.id} className="short-row">
              <InputBox
                label={i === 0 ? "YouTube Short Link" : ""}
                field={`vs_${item.id}`}
                type="text"
                placeholder="https://youtube.com/shorts/..."
                value={item.short_link || ""}
                onChange={(_, val) => updShort(item.id, val)}
              />
              <button type="button" className="short-rm" onClick={() => rmShort(item.id)}>×</button>
            </div>
          ))}</div>
        }
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAP PICKER MODAL
// ═══════════════════════════════════════════════════════════════
const MapPickerModal = ({ onSelect, onClose, initialLat, initialLng }) => {
  const mapDivRef    = useRef(null);
  const mapRef       = useRef(null);
  const markerRef    = useRef(null);
  const pickedLatRef = useRef(initialLat || 20.5937);
  const pickedLngRef = useRef(initialLng || 78.9629);
  const addressRef   = useRef("");
  const [pickedLat, setPickedLat] = useState(initialLat || 20.5937);
  const [pickedLng, setPickedLng] = useState(initialLng || 78.9629);
  const [address,   setAddress]   = useState("");
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const initMap = () => {
      if (!mapDivRef.current || mapRef.current) return;
      const L = window.L;
      const lat = initialLat || 20.5937, lng = initialLng || 78.9629;
      const map = L.map(mapDivRef.current, { zoomControl: true }).setView([lat, lng], initialLat ? 14 : 5);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap contributors" }).addTo(map);
      const icon = L.divIcon({ className: "", html: `<div style="width:28px;height:28px;background:#e63946;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.35);"></div>`, iconSize: [28,28], iconAnchor: [14,28] });
      const marker = L.marker([lat, lng], { draggable: true, icon }).addTo(map);
      markerRef.current = marker; mapRef.current = map;
      const reverseGeo = async (lt, lg) => {
        setLoading(true);
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lt}&lon=${lg}&format=json`, { headers: { "Accept-Language": "en" } });
          const data = await res.json();
          const addr = data?.display_name || "";
          setAddress(addr); addressRef.current = addr;
        } catch { setAddress(""); addressRef.current = ""; }
        setLoading(false);
      };
      const updatePos = (latlng) => {
        pickedLatRef.current = latlng.lat; pickedLngRef.current = latlng.lng;
        setPickedLat(latlng.lat); setPickedLng(latlng.lng);
        reverseGeo(latlng.lat, latlng.lng);
      };
      map.on("click", (e) => { marker.setLatLng(e.latlng); updatePos(e.latlng); });
      marker.on("dragend", () => { updatePos(marker.getLatLng()); });
      if (initialLat) reverseGeo(lat, lng);
    };
    if (window.L) initMap();
    else {
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = initMap;
      document.head.appendChild(script);
    }
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []); // eslint-disable-line

  return (
    <div className="map-modal-overlay" onClick={onClose}>
      <div className="map-modal" onClick={e => e.stopPropagation()}>
        <div className="map-modal-header">
          <h3 className="map-modal-title"><AppIcon name="mapPin" size={18} /> Pick Location</h3>
          <button className="map-modal-close" onClick={onClose}>×</button>
        </div>
        <p className="map-modal-hint">Click anywhere on the map or drag the pin to set location.</p>
        <div ref={mapDivRef} className="map-leaflet-container" />
        <div className="map-modal-coords">
          <span className="map-coord-badge">
            {typeof pickedLat === "number" ? pickedLat.toFixed(5) : pickedLat},{" "}
            {typeof pickedLng === "number" ? pickedLng.toFixed(5) : pickedLng}
          </span>
          {loading && <span className="map-coord-loading">Fetching address…</span>}
          {!loading && address && <span className="map-address-preview">{address}</span>}
        </div>
        <div className="map-modal-footer">
          <button type="button" className="map-modal-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="map-modal-confirm" onClick={() => onSelect({ lat: pickedLatRef.current, lng: pickedLngRef.current, address: addressRef.current })}>Confirm Location</button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// LOCATION BLOCK
// ═══════════════════════════════════════════════════════════════
export const LocationBlock = ({ formData, onChange, errors }) => {
  const { states, cities, subregisters } = useContext(MyContext);
  const [mapOpen, setMapOpen] = useState(false);

  const st   = Array.isArray(states)       ? states       : [];
  const ct   = Array.isArray(cities)       ? cities       : [];
  const subs = Array.isArray(subregisters) ? subregisters : [];

  const fCities = ct.filter(c => c.state_id === formData.state_id);
  const fSubs   = subs.filter(s => s.city_id === formData.city_id);

  const handleState = (f, v) => {
    onChange("state_id", v); onChange("city_id", "");
    onChange("sub_register_id", ""); onChange("map_location_name", "");
  };
  const handleCity = (f, v, opt) => {
    onChange("city_id", v); onChange("sub_register_id", "");
    const sn = st.find(s => s.id === formData.state_id)?.state_name || "";
    onChange("map_location_name", `${opt?.city_name || ""}, ${sn}`);
  };

  const lat = parseFloat(formData.latitude);
  const lng = parseFloat(formData.longitude);
  const hasLatLng = !isNaN(lat) && !isNaN(lng);
  const mapSrc = hasLatLng
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.02},${lat-0.02},${lng+0.02},${lat+0.02}&layer=mapnik&marker=${lat},${lng}`
    : null;

  const handleMapSelect = ({ lat: selLat, lng: selLng, address }) => {
    onChange("latitude", selLat); onChange("longitude", selLng);
    if (address) { onChange("map_location_name", address); onChange("address", address); }
    setMapOpen(false);
  };

  return (
    <div className="location-wrap">
      <div className="loc-row3">
        <Dropdwon label="State" field="state_id" options={st}
          value={formData.state_id} onChange={handleState}
          placeholder="Select state" isrequired labelKey="state_name" valueKey="id" error={errors?.state_id} />
        <Dropdwon label="City" field="city_id" options={fCities}
          value={formData.city_id} onChange={handleCity}
          placeholder="Select city" isrequired labelKey="city_name" valueKey="id" error={errors?.city_id} />
        <Dropdwon label="Sub Register" field="sub_register_id" options={fSubs}
          value={formData.sub_register_id} onChange={(f, v) => onChange(f, v)}
          placeholder="Sub Register" labelKey="sub_name" valueKey="id" />
      </div>
      <div className="map-outer">
        <div className="map-header-row">
          <span className="map-header-label"><AppIcon name="mapPin" size={14} /> Location on Map</span>
          <button type="button" className="map-pick-btn" onClick={() => setMapOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            {hasLatLng ? "Change Location" : "Pick on Map"}
          </button>
        </div>
        {hasLatLng && mapSrc ? (
          <div className="map-preview-wrap" onClick={() => setMapOpen(true)}>
            <iframe key={mapSrc} title="Map Preview" className="map-iframe map-iframe-preview"
              src={mapSrc} allowFullScreen loading="lazy" style={{ pointerEvents: "none" }} />
            <div className="map-preview-overlay"><span className="map-preview-overlay-label">Click to change location</span></div>
          </div>
        ) : (
          <button type="button" className="map-empty-pick" onClick={() => setMapOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            <span>Click to pick location on map</span>
          </button>
        )}
        {hasLatLng && (
          <div className="map-coords-row">
            <span className="map-coord-badge"><AppIcon name="mapPin" size={12} /> {lat.toFixed(5)}, {lng.toFixed(5)}</span>
            {formData.map_location_name && <span className="map-geocoded-text">{formData.map_location_name}</span>}
          </div>
        )}
      </div>
      <InputBox label="Address" field="address" type="textarea" placeholder="Enter full address"
        value={formData.address || ""} onChange={onChange} isrequired error={errors?.address} />
      <InputBox label="Address Details / Landmark" field="address_details" type="textarea"
        placeholder="Landmark, directions..." value={formData.address_details || ""} onChange={onChange} />
      {mapOpen && (
        <MapPickerModal initialLat={hasLatLng ? lat : null} initialLng={hasLatLng ? lng : null}
          onSelect={handleMapSelect} onClose={() => setMapOpen(false)} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// KV ADD MORE
// ═══════════════════════════════════════════════════════════════
export const KVAddMore = ({ block, formData, onChange }) => {
  const { nearestlocation } = useContext(MyContext);
  const nl  = Array.isArray(nearestlocation) ? nearestlocation : [];
  const key  = block.block_key;
  const rows = formData[key] || [{ [block.left_field]: "", [block.right_field]: "" }];
  const upd  = (idx, f, v) => onChange(key, rows.map((r, i) => i === idx ? { ...r, [f]: v } : r));
  const add  = () => onChange(key, [...rows, { [block.left_field]: "", [block.right_field]: "" }]);
  const rm   = idx => onChange(key, rows.filter((_, i) => i !== idx));

  return (
    <div className="kv-addmore">
      {rows.map((row, idx) => (
        <div key={idx} className="kv-row">
          <div>
            {block.left_type === "nearest_dropdown"
              ? <Dropdwon field={block.left_field} options={nl}
                  value={row[block.left_field]} onChange={(_, v) => upd(idx, block.left_field, v)}
                  placeholder={block.left_label} labelKey="near_name" valueKey="id" />
              : <InputBox field={block.left_field} placeholder={block.left_label}
                  value={row[block.left_field]} onChange={(_, v) => upd(idx, block.left_field, v)} />
            }
          </div>
          <div>
            <InputBox field={block.right_field} type={block.right_type}
              placeholder={block.right_label} value={row[block.right_field]}
              onChange={(_, v) => upd(idx, block.right_field, v)} />
          </div>
          {rows.length > 1 && (
            <button type="button" className="kv-rm" onClick={() => rm(idx)}>×</button>
          )}
        </div>
      ))}
      <button type="button" className="add-more-btn" onClick={add}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>Add More
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PLAN BLOCK
// ═══════════════════════════════════════════════════════════════
export const PlanBlock = ({ formData, onChange }) => {
  const plans = formData.property_plan || [];
  const upd  = (id, k, v) => onChange("property_plan", plans.map(p => p.id === id ? { ...p, [k]: v } : p));
  const add  = () => onChange("property_plan", [...plans, { id: Date.now(), image: null, title: "", description: "" }]);
  const rm   = id => onChange("property_plan", plans.filter(p => p.id !== id));
  return (
    <div className="plan-addmore">
      {plans.map((plan, idx) => (
        <div key={plan.id} className="plan-item">
          <div className="plan-header">
            <span className="plan-num">Plan {idx + 1}</span>
            <button type="button" className="plan-rm" onClick={() => rm(plan.id)}>Remove</button>
          </div>
          <div className="plan-grid">
            <FileUpload
              label="Plan Image"
              field={`pi_${plan.id}`}
              value={plan.image}
              onChange={(_, val) => upd(plan.id, "image", val)}
              multi_upload={false}
              accept="image/*"
            />
            <div className="plan-fields">
              <InputBox label="Title" field={`pt_${plan.id}`} placeholder="Enter plan title"
                value={plan.title} onChange={(_, v) => upd(plan.id, "title", v)} />
              <InputBox label="Description" field={`pd_${plan.id}`} type="textarea"
                placeholder="Describe this plan" value={plan.description}
                onChange={(_, v) => upd(plan.id, "description", v)} />
            </div>
          </div>
        </div>
      ))}
      <button type="button" className="add-more-btn" onClick={add}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>Add More Plan
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPANY PROFILE BLOCK
// fields: company_logo, company_name, company_description, company_id
// Selecting an existing company locks logo/name and auto-fills logo.
// ═══════════════════════════════════════════════════════════════
export const CompanyProfileBlock = ({ formData, onChange, errors }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const locked = !!formData.company_id;

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    const trimmed = String(q || '').trim();
    if (trimmed.length < 1) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoadingSuggest(true);
    try {
      const res = await API.get('/companies/suggest', { params: { q: trimmed, limit: 8 } });
      const list = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
      setSuggestions(list);
      setOpen(true);
    } catch {
      setSuggestions([]);
    } finally {
      setLoadingSuggest(false);
    }
  }, []);

  const handleNameChange = (field, value) => {
    onChange('company_id', null);
    onChange('company_is_new', true);
    onChange('company_logo', '');
    onChange(field, value);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(value), 220);
  };

  const selectCompany = (item) => {
    const hasId = item?.id != null && Number(item.id) > 0;
    onChange('company_id', hasId ? item.id : null);
    onChange('company_name', item.name || item.value || item.label || '');
    onChange('company_logo', item.logo || item.logo_url || '');
    onChange('company_description', item.description || '');
    onChange('company_is_new', !hasId);
    setSuggestions([]);
    setOpen(false);
  };

  const clearLinkedCompany = () => {
    onChange('company_id', null);
    onChange('company_is_new', true);
    onChange('company_logo', '');
    onChange('company_description', '');
  };

  return (
    <div className="company-profile-wrap" ref={wrapRef}>
      {locked ? (
        <div className="cp-selected-card">
          <div className="cp-selected-top">
            <div className="cp-selected-logo">
              {formData.company_logo ? (
                <img
                  src={
                    formData.company_logo instanceof File
                      ? URL.createObjectURL(formData.company_logo)
                      : companyLogoSrc(formData.company_logo)
                  }
                  alt=""
                />
              ) : (
                <span>{(formData.company_name || 'C').slice(0, 2).toUpperCase()}</span>
              )}
            </div>
            <div className="cp-selected-meta">
              <span className="cp-selected-badge">Linked company</span>
              <strong className="cp-selected-name">{formData.company_name || 'Selected company'}</strong>
              {String(formData.company_description || '').trim() ? (
                <p className="cp-selected-hint cp-selected-desc">
                  {formData.company_description}
                </p>
              ) : (
                <p className="cp-selected-hint">Name and logo are locked. Change to pick a different company.</p>
              )}
            </div>
          </div>
          <button type="button" className="cp-selected-change" onClick={clearLinkedCompany}>
            Change company
          </button>
        </div>
      ) : (
        <div className="cp-photo-row">
          <div className="cp-photo-col">
            <FileUpload
              label="Company Logo / Profile Photo"
              field="company_logo"
              value={formData.company_logo}
              onChange={onChange}
              multi_upload={false}
              accept="image/*"
              error={errors?.company_logo}
            />
          </div>
          <div className="cp-info-col">
            <div className="cp-suggest-wrap">
              <label className="cp-field-label">Company / Developer Name</label>
              <div className="cp-suggest-input-row">
                <input
                  className={`cp-suggest-input${errors?.company_name ? ' has-error' : ''}`}
                  type="text"
                  placeholder="Search or enter company name"
                  value={formData.company_name || ''}
                  onChange={(e) => handleNameChange('company_name', e.target.value)}
                  onFocus={() => {
                    if ((formData.company_name || '').trim()) fetchSuggestions(formData.company_name);
                  }}
                  autoComplete="off"
                />
              </div>
              {errors?.company_name && <div className="error-text">{errors.company_name}</div>}
              {open && (
                <div className="cp-suggest-dropdown">
                  {loadingSuggest && <div className="cp-suggest-empty">Searching…</div>}
                  {!loadingSuggest && suggestions.length === 0 && (
                    <div className="cp-suggest-empty">No existing company — a new company will be created.</div>
                  )}
                  {suggestions.map((item) => (
                    <button
                      key={item.id || item.name_key || item.name}
                      type="button"
                      className="cp-suggest-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectCompany(item);
                      }}
                    >
                      <span className="cp-suggest-logo">
                        {(item.logo_url || item.logo) ? (
                          <img src={item.logo_url || companyLogoSrc(item.logo)} alt="" />
                        ) : (
                          <span>{(item.name || '?').slice(0, 1).toUpperCase()}</span>
                        )}
                      </span>
                      <span className="cp-suggest-copy">
                        <strong>{item.name}</strong>
                        <small>
                          {item.listing_count
                            ? `${item.listing_count} listing${item.listing_count === 1 ? '' : 's'}`
                            : (item.description || 'Company / Developer')}
                        </small>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <InputBox
        label="Company Description"
        field="company_description"
        type="textarea"
        placeholder="Brief about the developer / company..."
        value={formData.company_description || ""}
        onChange={onChange}
        error={errors?.company_description}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// DRAFT MODAL
// ═══════════════════════════════════════════════════════════════
export const DraftModal = ({ onRestore, onDiscard }) => (
  <div className="modal-overlay">
    <div className="modal-box">
      <span className="modal-emoji"><AppIcon name="draft" size={32} /></span>
      <h3 className="modal-title">Draft Found</h3>
      <p className="modal-desc">You have an unsaved draft. Restore it or start fresh.</p>
      <div className="modal-actions">
        <button className="modal-btn restore" onClick={onRestore}>Restore Draft</button>
        <button className="modal-btn discard" onClick={onDiscard}>Start Fresh</button>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════
export const ICONS = {
  info:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  image:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  video:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
  location:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  amenities: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  nearest:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  additional:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
  plan:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
  seo:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  // ✅ Document icon
  document:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  // ✅ Company icon
  company:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
};
export const IC = { location: "loc-icon", video: "vid-icon", amenities: "amin-icon" };